import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { normalizeCode, type OrgExamType } from "@petergok/blue-collar-shared";
import { redeemOrgAccessCode } from "@/api/orgPurchases";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface RedeemCodeModalProps {
  examType: OrgExamType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLegacyRedeem?: (code: string) => Promise<string>;
  onRedeemed?: (code: string) => Promise<boolean | void> | boolean | void;
  getBlockedReason?: () => string | null;
  initialCode?: string;
  initialErrorReason?: string;
}

function useErrorMessageResolvers() {
  const { t } = useTranslation();

  const getErrorMessage = useCallback(
    (reason: string) => {
    switch (reason) {
      case "not_found":
        return t("errors.redeem_not_found_long");
      case "already_redeemed":
        return t("errors.redeem_already_redeemed_long");
        case "already_has_access":
          return t("errors.redeem_already_has_access");
      case "wrong_exam_type":
        return t("errors.redeem_wrong_exam_type");
      case "rate_limited":
        return t("errors.redeem_rate_limited");
      // We stopped waiting; the server may still have completed it. Saying "we could
      // not redeem that code" would be a claim we cannot support, and would push the
      // student to re-enter a code that may already be theirs.
      case "timed_out":
        return t("errors.redeem_timed_out");
      default:
        return t("errors.redeem_unknown");
    }
    },
    [t],
  );

  const getLegacyErrorMessage = useCallback(
    (result: string) => {
    switch (result) {
      case "NOT_FOUND":
        return t("errors.redeem_not_found_long");
      case "ALREADY_REDEEMED":
        return t("errors.redeem_already_redeemed_long");
      default:
        return t("errors.redeem_unknown");
    }
    },
    [t],
  );

  return { getErrorMessage, getLegacyErrorMessage };
}

export default function RedeemCodeModal({
  examType,
  open,
  onOpenChange,
  onLegacyRedeem,
  onRedeemed,
  getBlockedReason,
  initialCode = "",
  initialErrorReason = "",
}: RedeemCodeModalProps) {
  const { t } = useTranslation();
  const { getErrorMessage, getLegacyErrorMessage } = useErrorMessageResolvers();
  const [code, setCode] = useState(() => (open ? initialCode : ""));
  const [error, setError] = useState(() =>
    open && initialErrorReason ? getErrorMessage(initialErrorReason) : "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const previousInitialStateRef = useRef({
    initialCode,
    initialErrorReason,
    open,
  });

  const normalizedCode = useMemo(() => normalizeCode(code), [code]);

  async function completeRedemption() {
    const handledByParent = await onRedeemed?.(normalizedCode);
    if (handledByParent) {
      return true;
    }

    setIsSuccess(true);
    return false;
  }

  useEffect(() => {
    const previous = previousInitialStateRef.current;
    const opened = open && !previous.open;
    const closed = !open && previous.open;
    const initialStateChanged =
      open &&
      (initialCode !== previous.initialCode ||
        initialErrorReason !== previous.initialErrorReason);

    previousInitialStateRef.current = {
      initialCode,
      initialErrorReason,
      open,
    };

    if (opened || initialStateChanged) {
      setCode(initialCode);
      setError(initialErrorReason ? getErrorMessage(initialErrorReason) : "");
      setIsLoading(false);
      setIsSuccess(false);
      return;
    }

    if (closed) {
      setCode("");
      setError("");
      setIsLoading(false);
      setIsSuccess(false);
    }
  }, [getErrorMessage, initialCode, initialErrorReason, open]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!normalizedCode) return;

    const blockedReason = getBlockedReason?.();
    if (blockedReason) {
      setError(getErrorMessage(blockedReason));
      return;
    }

    setIsLoading(true);
    setError("");
    let handledByParent = false;

    try {
      const result = await redeemOrgAccessCode({
        code: normalizedCode,
        examType,
      });
      if ("reason" in result) {
        // The org redeem endpoint is the primary source of truth. Codes minted before
        // the org-purchases launch live in the legacy redemption store, so on
        // `not_found` we fall through to the legacy endpoint to keep electrical_us
        // users with pre-org codes working during the migration window. Drop the
        // `onLegacyRedeem` prop once those codes have all been migrated or expired.
        if (result.reason === "not_found" && onLegacyRedeem) {
          const legacyResult = await onLegacyRedeem(normalizedCode);
          if (legacyResult === "Success") {
            handledByParent = await completeRedemption();
            return;
          }

          setError(getLegacyErrorMessage(legacyResult));
          return;
        }

        setError(getErrorMessage(result.reason));
        return;
      }

      handledByParent = await completeRedemption();
    } catch (err) {
      setError(err instanceof Error ? err.message : getErrorMessage("unknown"));
    } finally {
      if (!handledByParent) {
        setIsLoading(false);
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden bg-white">
        <div className="p-6">
          <DialogHeader className="relative">
            <DialogTitle className="text-center text-xl font-medium">
              {isSuccess
                ? t("profile.redeem_org_success_title")
                : t("profile.redeem_org_modal_title")}
            </DialogTitle>
          </DialogHeader>

          {isSuccess ? (
            <div className="mt-4 space-y-4 text-center">
              <p className="text-sm text-zinc-600">
                {t("profile.redeem_org_success_body")}
              </p>
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full bg-[var(--dp-brand-button)] text-white hover:bg-[var(--dp-brand-hover)]"
              >
                {t("profile.redeem_done")}
              </Button>
            </div>
          ) : (
            <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
              <p className="text-center text-sm leading-6 text-zinc-600">
                {t("profile.redeem_org_modal_subtext")}
              </p>
              <div className="space-y-2">
                <label
                  htmlFor="org-access-code"
                  className="block text-sm font-medium"
                >
                  {t("profile.redeem_access_code_label")}
                </label>
                <Input
                  id="org-access-code"
                  value={code}
                  placeholder={t("profile.redeem_placeholder")}
                  onChange={(event) => setCode(event.target.value)}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  disabled={isLoading}
                />
                {error && (
                  <p role="alert" className="text-sm text-red-700">
                    {error}
                  </p>
                )}
              </div>
              <Button
                type="submit"
                disabled={!normalizedCode || isLoading}
                className={cn(
                  "w-full bg-[var(--dp-brand-button)] py-3 text-white hover:bg-[var(--dp-brand-hover)]",
                  (isLoading || !normalizedCode) &&
                    "bg-gray-200 hover:bg-gray-200 text-black",
                )}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("profile.redeem_org_submit")}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={isLoading}
                onClick={() => onOpenChange(false)}
                className="w-full border-[var(--dp-brand-button)] text-[var(--dp-brand-button)] hover:bg-[var(--dp-brand-soft)] hover:text-[var(--dp-brand-button)]"
              >
                {t("common.cancel")}
              </Button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
